// @ts-check
/**
 * Main storefront script. Uses the `ShopTheme` global for cart, product forms, menus,
 * quick buy, and collection UI. For declarative drawers/modals via data attributes,
 * see `theme-api.js` (`ThemeAPI` + `data-action` / `data-component`).
 */
var ShopTheme = ShopTheme || {};

// Event delegation helper to prevent duplicate listeners
ShopTheme.EventDelegation = {
	registeredListeners: new Set(),
	
	addListener: function(eventType, selector, handler, options = {}) {
		const key = `${eventType}-${selector}`;
		
		if (this.registeredListeners.has(key)) {
			if (ShopTheme.Global.DEBUG) console.warn(`Event listener already registered for ${key}`);
			return;
		}
		
		this.registeredListeners.add(key);
		
		document.addEventListener(eventType, function(e) {
			if (e.target.matches(selector) || (options.useClosest && e.target.closest(selector))) {
				const target = options.useClosest ? e.target.closest(selector) : e.target;
				handler.call(target, e, target);
			}
		});
		
		if (ShopTheme.Global.DEBUG) console.log(`Registered delegated listener: ${key}`);
	}
};

ShopTheme.Global = {
	DEBUG: false,
	_modalBackdropListenerAttached: false,
	pageUnderlay: null,
	getPageOverlay: function () {
		var element =
			ShopTheme.Global.pageUnderlay ||
			document.querySelector('[data-component="overlay"]') ||
			document.querySelector("[data-underlay]");
		if (element) {
			ShopTheme.Global.pageUnderlay = element;
		}
		return element;
	},
	pageModal: document.querySelector("[data-modal-container]"),
	toggleVisibility: function (element, isVisible) {
		if (isVisible) {
			element.classList.remove("hidden");
		} else {
			element.classList.add("hidden");
		}
	},
	fixBodyScroll: function (isFixed) {
		if (isFixed) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}
	},
	toggleModal: function (isVisible) {
		var element =
			ShopTheme.Global.pageModal ||
			document.querySelector("[data-modal-container]");
		if (element) {
			ShopTheme.Global.pageModal = element;
		}
		var body = document.body;
		if (!element || !body) {
			return;
		}
		if (isVisible) {
			if (!ShopTheme.Global._modalBackdropListenerAttached) {
				ShopTheme.Global._modalBackdropListenerAttached = true;
				element.addEventListener("click", function (event) {
					if (event.target === element) {
						ShopTheme.Global.toggleModal(false);
					}
				});
			}
			ShopTheme.Global.fixBodyScroll(true);
			element.classList.remove("hidden");
		} else {
			ShopTheme.Global.fixBodyScroll(false);
			element.classList.add("hidden");
		}
	},
	toggleOverlay: function (isVisible) {
		var element = ShopTheme.Global.getPageOverlay();
		var body = document.body;
		if (!element || !body) {
			return;
		} else {
			if (ShopTheme.Global.DEBUG) console.log("Toggling overlay");
			if (isVisible) {
				if (ShopTheme.Global.DEBUG) console.log("Showing overlay");
				ShopTheme.Global.fixBodyScroll(true);
				element.classList.remove("hidden");
			} else {
				if (ShopTheme.Global.DEBUG) console.log("Hiding overlay");
				ShopTheme.Global.fixBodyScroll(false);
				element.classList.add("hidden");
			}
		}
	},
	toggleCartDrawer: function (isVisible) {
		if (this.DEBUG) console.log("toggleCartDrawer called");

		if (
			typeof ThemeAPI !== "undefined" &&
			ThemeAPI.Components &&
			ThemeAPI.Components.api
		) {
			if (isVisible) {
				ThemeAPI.Components.api.open("cart-drawer");
			} else {
				ThemeAPI.Components.api.close("cart-drawer");
			}
			return;
		}

		var cartDrawer = document.getElementById("cartDrawer");
		if (!cartDrawer) {
			return;
		}

		if (isVisible) {
			if (this.DEBUG) console.log("Opening cart drawer");
			cartDrawer.classList.remove("translate-x-full", "invisible");
			this.toggleOverlay(true);
		} else {
			if (this.DEBUG) console.log("Closing cart drawer");
			cartDrawer.classList.add("translate-x-full", "invisible");
			this.toggleOverlay(false);
		}
	},
	closeAllOverlayComponents: function () {
		if (this.DEBUG) console.log("Closing all overlay components");
		var pageModal =
			ShopTheme.Global.pageModal ||
			document.querySelector("[data-modal-container]");
		if (pageModal && !pageModal.classList.contains("hidden")) {
			ShopTheme.Global.toggleModal(false);
		}

		var cartDrawer = document.getElementById("cartDrawer");
		if (
			cartDrawer &&
			!cartDrawer.classList.contains("translate-x-full") &&
			!cartDrawer.classList.contains("invisible")
		) {
			ShopTheme.Global.toggleCartDrawer(false);
		}

		var mobileMenu = document.querySelector(".mobile-menu");
		if (mobileMenu && mobileMenu.classList.contains("open")) {
			ShopTheme.MobileMenu.toggleMenu(false);
		}
	},
};

ShopTheme.Menu = (function () {
	function hideAllMenus() {
		document.querySelectorAll("ul[data-child-menu]").forEach(function (menu) {
			menu.classList.add("hidden");
		});
	}

	function setupMenuToggle() {
		document.addEventListener("click", function (event) {
			var link = event.target.closest(
				'li[data-has-child-menu="true"] > a'
			);
			if (!link) {
				return;
			}
			var parentLi = link.parentNode;
			var childMenu = parentLi && parentLi.querySelector("ul[data-child-menu]");
			if (!childMenu) {
				return;
			}
			var isMenuHidden = childMenu.classList.contains("hidden");

			hideAllMenus();

			if (isMenuHidden) {
				event.preventDefault();
				childMenu.classList.remove("hidden");
			}
		});
	}

	function setupDocumentClickListener() {
		document.addEventListener("click", function (event) {
			var isClickInsideMenu =
				event.target.closest("ul[data-child-menu]") ||
				event.target.closest('li[data-has-child-menu="true"]');
			if (!isClickInsideMenu) {
				hideAllMenus();
			}
		});
	}

	function init() {
		setupMenuToggle();
		setupDocumentClickListener();
	}

	// Public API
	return {
		init: init,
	};
})();

ShopTheme.MobileMenu = (function () {
	var underlay,
		menu,
		backButton,
		openMenuButton,
		breadcrumbsContainer,
		menuItems;

	function toggleMenu(open) {
		if (!menu) {
			return;
		}
		menu.classList.toggle("open", open);
		ShopTheme.Global.toggleOverlay(open);
		document.body.classList.toggle("overflow-hidden", open);
		if (open) {
			updateBackButton();
		} else {
			closeAllChildMenus();
			updateBreadcrumbs();
		}
	}

	function closeAllChildMenus() {
		const openChildMenus = document.querySelectorAll(".mobile-sub-menu.open");
		openChildMenus.forEach(function (menu) {
			menu.classList.remove("open");
		});
	}

	function updateBackButton() {
		const openSubMenus = document.querySelectorAll(".mobile-sub-menu.open");
		backButton.innerHTML = openSubMenus.length > 0 ? "&lt; Back" : "&#10005;";
	}

	function updateBreadcrumbs(selectedItem) {
		if (selectedItem) {
			const primaryTitle = selectedItem.textContent.trim().split("\n")[0];
			breadcrumbsContainer.innerHTML = `${primaryTitle}`;
		} else {
			const openSubMenus = document.querySelectorAll(".mobile-sub-menu.open");
			const parentMenuItemTitle =
				openSubMenus.length > 0
					? openSubMenus[openSubMenus.length - 1]
							.closest("li")
							.textContent.trim()
							.split("\n")[0]
					: "Home";
			breadcrumbsContainer.innerHTML = `${parentMenuItemTitle}`;
		}
	}

	function handleMenuItemClick(e) {
		e.stopPropagation();
		const menuItem = e.currentTarget;
		if (menuItem.dataset.hasChildMenu === "true") {
			e.preventDefault();
			menuItem.querySelector("[data-mobile-child-menu]").classList.add("open");
		} else {
			toggleMenu(false);
		}
		updateBreadcrumbs(menuItem);
		updateBackButton();
	}

	function handleBackButtonClick() {
		const openSubMenus = document.querySelectorAll(".mobile-sub-menu.open");
		const shouldCloseMenu = openSubMenus.length === 0;
		if (shouldCloseMenu) {
			toggleMenu(false);
		} else {
			openSubMenus[openSubMenus.length - 1].classList.remove("open");
			updateBreadcrumbs();
		}
	}

	function setupEventListeners() {
		openMenuButton.addEventListener("click", () => toggleMenu(true));
		backButton.addEventListener("click", handleBackButtonClick);
		underlay.addEventListener("click", function () {
			if (menu.classList.contains("open")) {
				toggleMenu(false); // Only toggle the menu if it's currently open
			}
		});
		menuItems.forEach((menuItem) =>
			menuItem.addEventListener("click", handleMenuItemClick)
		);
	}

	function init() {
		underlay = document.querySelector("[data-underlay]");
		menu = document.querySelector(".mobile-menu");
		backButton = document.querySelector("[data-back-button]");
		openMenuButton = document.querySelector("[data-open-mobile-menu]");
		breadcrumbsContainer = document.querySelector(".breadcrumbs");
		menuItems = document.querySelectorAll(".mobile-menu-items li");

		if (
			underlay &&
			menu &&
			backButton &&
			openMenuButton &&
			breadcrumbsContainer
		) {
			setupEventListeners();
		}
	}

	return {
		init: init,
		toggleMenu: toggleMenu,
	};
})();

ShopTheme.Cart = (function () {
	function addItem(itemId, quantity) {
		var addUrl = (localRoutes.cartAddRoute || "/cart/add") + ".js";

		fetch(addUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				items: [
					{
						id: itemId,
						quantity: quantity,
					},
				],
				sections: "main-cart,header,cart-drawer",
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				var cartUpdatedEvent = new CustomEvent("cart:updated", {
					detail: data,
				});
				document.dispatchEvent(cartUpdatedEvent);
			})
			.catch((error) => {
				console.error("Error adding item:", error);
				var cartUpdatedEvent = new CustomEvent("cart:updated", {
					detail: error,
				});
				document.dispatchEvent(cartUpdatedEvent);
			});
	}

	function updateItem(itemId, quantity) {
		var updateUrl = (localRoutes.cartUpdateRoute || "/cart/update") + ".js";

		fetch(updateUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				updates: {
					[itemId]: quantity,
				},
				sections: "main-cart,header,cart-drawer",
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				// Additional logic after updating item quantity
				var cartUpdatedEvent = new CustomEvent("cart:updated", {
					detail: data,
				});
				document.dispatchEvent(cartUpdatedEvent);
			})
			.catch((error) => {
				console.error("Error updating item:", error);
				var cartUpdatedEvent = new CustomEvent("cart:updated", {
					detail: error,
				});
				document.dispatchEvent(cartUpdatedEvent);
			});
	}

	function submitForm(formData) {
		var addUrl = (localRoutes.cartAddRoute || "/cart/add") + ".js";
		formData.append("sections", "header,cart-drawer");
		fetch(addUrl, {
			method: "POST",
			body: formData,
		})
			.then((response) => response.json())
			.then((data) => {
				if (ShopTheme.Global.DEBUG) console.log("Cart updated:", data);
				var cartUpdatedEvent = new CustomEvent("cart:updated", {
					detail: data,
				});
				document.dispatchEvent(cartUpdatedEvent);
			})
			.catch((error) => {
				console.error("Error updating cart:", error);
				var cartUpdatedEvent = new CustomEvent("cart:updated", {
					detail: error,
				});
				document.dispatchEvent(cartUpdatedEvent);
			});
	}

	return {
		addItem: addItem,
		updateItem: updateItem,
		submitForm: submitForm,
	};
})();

ShopTheme.ProductForm = (function () {
	function init() {
		setupProductFormListener();
	}

	function setupProductFormListener() {
		if (ShopTheme.Global.DEBUG) console.log("Setting up product form listener");
		
		// Delegated event listener for product form submissions
		document.addEventListener("submit", function (e) {
			if (e.target.matches('form[action="/cart/add"]')) {
				if (ShopTheme.Global.DEBUG) console.log("Product form submitted", e);
				e.preventDefault();

				const form = e.target;
				const variantFieldsets = form.querySelectorAll("fieldset");
				if (variantFieldsets.length > 0) {
					const allSelected = Array.from(variantFieldsets).every(
						(fieldset) => fieldset.querySelector('input[type="radio"]:checked')
					);
					if (!allSelected) {
						return;
					}
				}

				const formData = new FormData(form);
				ShopTheme.Cart.submitForm(formData);
			}
		});
	}

	return {
		init: init,
		setupProductFormListener: setupProductFormListener,
	};
})();

ShopTheme.Cart.updateSections = function (sections, callback) {
	if (!sections) {
		return;
	} else {
		Object.keys(sections).forEach(function (sectionKey) {
			var newSectionHtml = sections[sectionKey];
			var parser = new DOMParser();
			var newSectionDoc = parser.parseFromString(newSectionHtml, "text/html");

			newSectionDoc
				.querySelectorAll("[data-section-target]")
				.forEach(function (newSectionPart) {
					var targetAttr = newSectionPart.getAttribute("data-section-target");
					var currentSectionPart = document.querySelector(
						'[data-section-target="' + targetAttr + '"]'
					);

					if (currentSectionPart) {
						currentSectionPart.replaceWith(newSectionPart);
					}
				});
		});
	}
	ShopTheme.Cart.setupCartEventListeners();
	if (typeof callback === "function") {
		callback(); // Execute the callback
	}
};

/* Product variant selection + section updates */
ShopTheme.Product = (function () {
	var _variantRadioDelegationBound = false;

	function getProductForm() {
		return document.querySelector('form[action="/cart/add"]');
	}

	function getVariantFieldsets(form) {
		var productForm = form || getProductForm();
		if (!productForm) {
			return [];
		}
		return Array.from(productForm.querySelectorAll("fieldset"));
	}

	function init() {
		setupRadioButtons();
		setupVariantChangeListener();
		updateSubmitButtonState();
	}

	function setupVariantChangeListener() {
		const variantSelect = document.getElementById("variant-select");
		if (variantSelect) {
			variantSelect.addEventListener("change", onVariantChange);
		}
	}

	function onVariantChange() {
		if (allFieldsetsHaveSelection()) {
			const variantSelect = document.getElementById("variant-select");
			const productPageSection = document.querySelector(
				'[data-section-target="main-product"]'
			);
			const quickBuySection = document.querySelector(
				'[data-section-target="product-quick-buy"]'
			);
			if (variantSelect) {
				// If the variantSelect exists get its value
				const variantId = variantSelect.value;
				if (!variantId) {
					return;
				}
				if (ShopTheme.Global.DEBUG)
					console.log("Variant changed to:", variantId);

				// Update the URL with the new variant ID
				if (window.location.pathname.includes("/products/")) {
					if (ShopTheme.Global.DEBUG)
						console.log("Updating URL with variant ID", variantId);
					const newUrl =
						window.location.protocol +
						"//" +
						window.location.host +
						window.location.pathname +
						"?variant=" +
						variantId;
					window.history.replaceState({ path: newUrl }, "", newUrl);
				}

				// Update the sections required when a variant changes
				ShopTheme.ProductGallery.syncFromVariantSelect();

				if (productPageSection) {
					updateProductSection(variantId);
				} else if (quickBuySection) {
					updateQuickBuySection(variantId);
				}
			}
		}
	}

	function updateProductSection(variantId) {
		if (ShopTheme.Global.DEBUG)
			console.log("Updating product section", variantId);
		// Update to use the active section rather than
		const sectionId = document
			.querySelector('[data-section-target="main-product"]')
			.getAttribute("data-sectionId");
		const url = new URL(window.location.href);
		url.searchParams.set("section_id", sectionId);
		if (variantId) {
			url.searchParams.set("variant", variantId);
		}
		fetch(url.toString())
			.then((response) => response.text())
			.then((html) => {
				const newSection = new DOMParser()
					.parseFromString(html, "text/html")
					.querySelector('[data-section-target="main-product"]');
				const currentSection = document.querySelector(
					'[data-section-target="main-product"]'
				);
				if (currentSection && newSection) {
					currentSection.replaceWith(newSection);
				}
				reattachEventListeners(); // Reattach event listeners to new DOM elements
				updateSubmitButtonState();
			});
	}

	function updateQuickBuySection(variantId) {
		if (ShopTheme.Global.DEBUG)
			console.log("Updating quick buy section", variantId);
		const sectionId = document
			.querySelector('[data-section-target="product-quick-buy"]')
			.getAttribute("data-sectionId");
		const productHandle = document
			.querySelector('[data-section-target="product-quick-buy"]')
			.getAttribute("data-productHandle");
		const url = `${window.location.origin}/products/${productHandle}?variant=${variantId}&section_id=${sectionId}`;
		if (ShopTheme.Global.DEBUG) console.log("Fetching URL:", url);
		fetch(url)
			.then((response) => response.text())
			.then((html) => {
				const newSection = new DOMParser()
					.parseFromString(html, "text/html")
					.querySelector('[data-section-target="product-quick-buy"]');
				const currentSection = document.querySelector(
					'[data-section-target="product-quick-buy"]'
				);
				console.log("Current section:", currentSection);
				console.log("New section:", newSection);
				if (currentSection && newSection) {
					currentSection.replaceWith(newSection);
				}
				reattachEventListeners(); // Reattach event listeners to new DOM elements
				updateSubmitButtonState();
			});
	}

	function reattachEventListeners() {
		setupRadioButtons(); // Reattach event listeners for radio buttons
		setupVariantChangeListener(); // Reattach the variant change listener
		ShopTheme.Cart.setupCartEventListeners(); // Reattach cart event listeners
		ShopTheme.ProductForm.setupProductFormListener();
	}

	function setupRadioButtons() {
		if (_variantRadioDelegationBound) {
			return;
		}
		_variantRadioDelegationBound = true;
		document.addEventListener("change", function (e) {
			if (e.target.matches('input[type="radio"]')) {
				onRadioButtonChange(e);
			}
		});
	}

	function onRadioButtonChange(e) {
		const form = e.target.closest('form[action="/cart/add"]');
		const variantSelect = form
			? form.querySelector("#variant-select")
			: document.getElementById("variant-select");

		if (!variantSelect) {
			updateSubmitButtonState(form);
			return;
		}

		if (allFieldsetsHaveSelection(form)) {
			const selectedOptions = getSelectedOptions(form);
			const matchingVariant = findMatchingVariant(
				selectedOptions,
				variantSelect
			);
			if (matchingVariant) {
				variantSelect.value = matchingVariant.value;
				variantSelect.dispatchEvent(new Event("change"));
			}
		} else {
			variantSelect.value = "";
		}

		updateSubmitButtonState(form);
	}

	function getSelectedOptions(form) {
		const options = {};
		getVariantFieldsets(form).forEach((fieldset) => {
			const checkedRadio = fieldset.querySelector(
				'input[type="radio"]:checked'
			);
			if (checkedRadio) {
				options[checkedRadio.name] = checkedRadio.value;
			}
		});
		return options;
	}

	function findMatchingVariant(selectedOptions, variantSelect) {
		return Array.from(variantSelect.options).find((option) => {
			if (!option.value) {
				return false;
			}
			return Object.keys(selectedOptions).every((key) => {
				const optionKey = "data-variant-" + key;
				return (
					!option.hasAttribute(optionKey) ||
					option.getAttribute(optionKey) === selectedOptions[key]
				);
			});
		});
	}

	function allFieldsetsHaveSelection(form) {
		const fieldsets = getVariantFieldsets(form);
		if (fieldsets.length === 0) {
			return true;
		}
		return fieldsets.every((fieldset) =>
			fieldset.querySelector('input[type="radio"]:checked')
		);
	}

	function updateSubmitButtonState(form) {
		const productForm = form || getProductForm();
		if (!productForm) {
			return;
		}

		const submitButton = productForm.querySelector("[data-add-to-cart]");
		if (!submitButton) {
			return;
		}

		const variantFieldsets = getVariantFieldsets(productForm);
		if (variantFieldsets.length === 0) {
			return;
		}

		submitButton.disabled = !allFieldsetsHaveSelection(productForm);
	}

	return {
		init: init,
	};
})();

ShopTheme.QuickBuy = (function () {
	var modalContainer = null;

	function closeQuickBuyModal(closeModal, closeOverlay) {
		if (ShopTheme.Global.DEBUG) console.log("Closing quick buy modal");
		if (closeOverlay) ShopTheme.Global.toggleOverlay(false);
		if (closeModal) ShopTheme.Global.toggleModal(false);
	}

	function handleCloseModalClick() {
		if (ShopTheme.Global.DEBUG) console.log("Close modal button clicked");
		closeQuickBuyModal(true, true);
	}

	function handleQuickBuyButtonClick(e, button) {
		if (ShopTheme.Global.DEBUG) console.log("Quick buy button clicked");
		e.stopImmediatePropagation();
		button = button || e.currentTarget;
		const onlyVariant = button.getAttribute("data-only-variant") === "true";
		const productHandle = button.getAttribute("data-product-handle");
		e.preventDefault();

		if (onlyVariant) {
			var variantId = button.getAttribute("data-variant-id");
			ShopTheme.Cart.addItem(variantId, 1);
		} else {
			const url = `${window.location.origin}/products/${productHandle}?section_id=product-quick-buy`;
			fetch(url)
				.then((response) => response.text())
				.then((html) => {
					modalContainer = document.querySelector("[data-modal-container]");
					if (modalContainer) {
						modalContainer.querySelector(
							"[data-modal-container-content]"
						).innerHTML = html;
						ShopTheme.Global.toggleOverlay(true);
						ShopTheme.Global.toggleModal(true);
						ShopTheme.Product.init();
						if (ShopTheme.Global.DEBUG)
							console.log("Reattaching event listeners");

						modalContainer
							.querySelectorAll("[data-close-modal]")
							.forEach((closeModalButton) => {
								closeModalButton.addEventListener(
									"click",
									handleCloseModalClick
								);
							});
					} else {
						console.error("Modal container not found");
					}
				})
				.catch((error) => console.error("Error loading the section:", error));
		}
	}

	function setupQuickBuyButtonListeners() {
		// Delegated event listener for quick buy buttons
		document.addEventListener("click", function (e) {
			if (e.target.matches('[data-quick-buy-button]') || e.target.closest('[data-quick-buy-button]')) {
				const button = e.target.matches('[data-quick-buy-button]') ? e.target : e.target.closest('[data-quick-buy-button]');
				handleQuickBuyButtonClick(e, button);
			}
		});
	}

	function init() {
		setupQuickBuyButtonListeners();
		// Listen for cart:updated event to close the quick buy modal when an item is added
		document.addEventListener("cart:updated", () =>
			closeQuickBuyModal(true, false)
		);
	}

	return {
		init: init,
	};
})();

ShopTheme.Cart._cartEventListenersBound = false;

ShopTheme.Cart.normalizeQuantityInput = function (input) {
	var quantity = parseInt(input.value, 10);
	var minQuantity = parseInt(input.getAttribute("min"), 10) || 1;

	if (isNaN(quantity) || quantity < minQuantity) {
		quantity = minQuantity;
		input.value = quantity;
	}

	return quantity;
};

ShopTheme.Cart.updateQuantityInput = function (input) {
	var itemId = input.getAttribute("data-update-item");
	var quantity = ShopTheme.Cart.normalizeQuantityInput(input);
	ShopTheme.Cart.updateItem(itemId, quantity);
};

ShopTheme.Cart.setupCartEventListeners = function () {
	if (ShopTheme.Cart._cartEventListenersBound) {
		return;
	}
	ShopTheme.Cart._cartEventListenersBound = true;

	if (ShopTheme.Global.DEBUG) console.log("Setting up cart event listeners");

	// Delegated event listeners for cart interactions
	document.addEventListener("click", function (e) {
		// Remove item buttons (use closest so label text / icons still trigger)
		const removeTrigger = e.target.closest("[data-remove-item]");
		if (removeTrigger) {
			const itemId = removeTrigger.getAttribute("data-remove-item");
			ShopTheme.Cart.updateItem(itemId, 0); // Remove item by setting quantity to 0
		}
		
		// Quantity selector buttons
		const quantityButton = e.target.closest(
			".increase-quantity, .decrease-quantity"
		);
		if (quantityButton && quantityButton.closest("[data-target-quantity-selectors]")) {
			e.preventDefault();
			let quantityInput;

			if (quantityButton.classList.contains("increase-quantity")) {
				quantityInput = quantityButton.previousElementSibling;
			} else if (quantityButton.classList.contains("decrease-quantity")) {
				quantityInput = quantityButton.nextElementSibling;
			}

			if (
				quantityInput &&
				quantityInput.classList.contains("quantity-item")
			) {
				let currentQuantity = parseInt(quantityInput.value, 10);
				const minQuantity =
					parseInt(quantityInput.getAttribute("min"), 10) || 1;

				if (quantityButton.classList.contains("increase-quantity")) {
					currentQuantity = isNaN(currentQuantity) ? minQuantity : currentQuantity + 1;
				} else if (
					quantityButton.classList.contains("decrease-quantity") &&
					currentQuantity > minQuantity
				) {
					currentQuantity--;
				}

				quantityInput.value = currentQuantity;
				ShopTheme.Cart.updateQuantityInput(quantityInput);
			}
		}
	});

	// Cart page quantity <select>
	document.addEventListener("change", function (e) {
		if (e.target.matches('select[data-update-item]')) {
			ShopTheme.Cart.updateQuantityInput(e.target);
		}
	});

	// Cart drawer quantity <input> — commit on unfocus
	document.addEventListener(
		"blur",
		function (e) {
			if (e.target.matches('input[data-update-item]')) {
				ShopTheme.Cart.updateQuantityInput(e.target);
			}
		},
		true
	);

	document.addEventListener("keydown", function (e) {
		if (e.key === "Enter" && e.target.matches('input[data-update-item]')) {
			e.target.blur();
		}
	});
};

ShopTheme.Global.setupGlobalEventListeners = function () {
	// Delegated event listeners for cart drawer buttons
	document.addEventListener("click", function (e) {
		// Close cart drawer buttons
		if (e.target.matches('#continueShopping, #closeCartDrawer')) {
			ShopTheme.Global.toggleCartDrawer(false);
		}
		
		// Cart toggle buttons
		if (e.target.closest('[data-toggle-cart]')) {
			e.preventDefault();
			e.stopPropagation();
			ShopTheme.Global.toggleCartDrawer(true);
		}
	});

	// Delegated overlay click handler
	document.addEventListener("click", function (e) {
		if (e.target.matches('[data-component="overlay"], [data-underlay]')) {
			ShopTheme.Global.closeAllOverlayComponents();
		}
	});

	function isCartPage() {
		return window.location.pathname.includes("/cart");
	}

	document.addEventListener("cart:updated", function (event) {
		ShopTheme.Cart.updateSections(event.detail.sections, function () {
			if (!isCartPage()) {
				ShopTheme.Global.toggleCartDrawer(true);
			}
		});
	});

};

ShopTheme.ProductGallery = (function () {
	var swipersBySection = {};

	function register(sectionId, swiper) {
		swipersBySection[sectionId] = swiper;
		syncFromVariantSelect();
	}

	function goToMedia(mediaId) {
		if (!mediaId) {
			return;
		}

		var mediaIdStr = String(mediaId);
		var gallery = document.querySelector("[data-product-gallery]");
		if (!gallery) {
			return;
		}

		var sectionId = gallery.getAttribute("data-section-id");
		var swiper = sectionId ? swipersBySection[sectionId] : null;
		if (!swiper) {
			return;
		}

		var slides = gallery.querySelectorAll(".swiper-slide[data-media-id]");
		var targetIndex = -1;
		slides.forEach(function (slide, index) {
			if (slide.getAttribute("data-media-id") === mediaIdStr) {
				targetIndex = index;
			}
		});

		if (targetIndex >= 0 && swiper.activeIndex !== targetIndex) {
			swiper.slideTo(targetIndex);
		}
	}

	function syncFromVariantSelect() {
		var variantSelect = document.getElementById("variant-select");
		if (!variantSelect) {
			return;
		}

		var selectedOption = variantSelect.options[variantSelect.selectedIndex];
		if (!selectedOption) {
			return;
		}

		var mediaId = selectedOption.getAttribute("data-featured-media-id");
		goToMedia(mediaId);
	}

	function init() {}

	return {
		init: init,
		register: register,
		goToMedia: goToMedia,
		syncFromVariantSelect: syncFromVariantSelect,
	};
})();

ShopTheme.ProductSwiper = (function () {
	function initializeSwipersInner() {
		const productCards = document.querySelectorAll(".product-card");

		productCards.forEach((card) => {
			const swiperEl = card.querySelector(".swiper");
			if (!swiperEl) {
				return;
			}

			if (swiperEl.swiper) {
				swiperEl.swiper.destroy(true, true);
			}

			const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
			if (slideCount < 2) {
				return;
			}

			const prevEl = card.querySelector(".product-card__swiper-prev");
			const nextEl = card.querySelector(".product-card__swiper-next");
			const autoHeight = swiperEl.classList.contains("product-swiper--natural");

			const config = {
				slidesPerView: 1,
				spaceBetween: 0,
				watchOverflow: true,
				autoHeight: Boolean(autoHeight),
				loop: false,
				rewind: true,
				observer: true,
				observeParents: true,
				pagination: false,
			};

			if (prevEl && nextEl) {
				config.navigation = {
					prevEl: prevEl,
					nextEl: nextEl,
				};
			}

			try {
				if (typeof window.Swiper !== "function") {
					return;
				}
				new window.Swiper(swiperEl, config);
			} catch (e) {
				if (typeof console !== "undefined" && console.warn) {
					console.warn("ProductSwiper:", e);
				}
			}
		});
	}

	function waitForSwiperThenInit() {
		var attempts = 0;
		function tick() {
			if (typeof window.Swiper === "function") {
				initializeSwipersInner();
				return;
			}
			if (attempts++ > 200) {
				return;
			}
			requestAnimationFrame(tick);
		}
		tick();
	}

	var _productSwiperSectionLoadBound = false;

	function init() {
		waitForSwiperThenInit();
		if (!_productSwiperSectionLoadBound) {
			_productSwiperSectionLoadBound = true;
			document.addEventListener("shopify:section:load", waitForSwiperThenInit);
		}
	}

	return {
		init: init,
		initializeSwipers: waitForSwiperThenInit,
	};
})();
ShopTheme.CollectionGrid = (function () {
	function init() {}
	return { init: init };
})();

document.addEventListener("DOMContentLoaded", function () {
	ShopTheme.Global.setupGlobalEventListeners();
	ShopTheme.Cart.setupCartEventListeners();
	ShopTheme.Product.init();
	ShopTheme.ProductGallery.init();
	ShopTheme.ProductForm.init();
	ShopTheme.Menu.init();
	ShopTheme.MobileMenu.init();
	ShopTheme.QuickBuy.init();
	ShopTheme.ProductSwiper.init();
	ShopTheme.CollectionGrid.init();
});

document.addEventListener("sectionRender:collection", (event) => {
	ShopTheme.ProductSwiper.initializeSwipers();
	ShopTheme.CollectionGrid.init();
});

